import React from 'react';
import { Divider, Typography, Space, Card } from 'antd';
import { GroupedFieldConfigurationDTO } from '@/shared/api/model';
import SingleFieldGroupForm from './SingleFieldGroupForm';

const { Title, Text } = Typography;

interface InlineFieldGroupsProps {
    fieldConfiguration: GroupedFieldConfigurationDTO;
    formData: any;
    onFieldChange: (fieldName: string, value: any) => void;
    errors?: Record<string, string>;
    readonly?: boolean;
    categoryId?: number; // Category ID for context
}

/**
 * InlineFieldGroups Component
 * 
 * Renders all field groups on a single page with dividers between them.
 * This provides a continuous scrolling experience instead of step-by-step navigation.
 * 
 * Use case: When users prefer to see all fields at once and fill them in any order.
 */
const InlineFieldGroups: React.FC<InlineFieldGroupsProps> = ({
    fieldConfiguration,
    formData,
    onFieldChange,
    errors = {},
    readonly = false,
    categoryId
}) => {
    const fieldGroups = fieldConfiguration.fieldGroups || [];
    const visibleGroups = fieldGroups
        .filter(group => group.isVisible !== false)
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

    if (visibleGroups.length === 0) {
        return (
            <Card>
                <Text type="secondary">No field groups configured for this category.</Text>
            </Card>
        );
    }

    return (
        <div className="inline-field-groups">
            {visibleGroups.map((group, index) => (
                <div key={group.groupKey || index}>
                    {/* Group Header with Icon */}
                    <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: 16 }}>
                        <Title level={4} style={{ marginBottom: 0 }}>
                            {group.icon && <span style={{ marginRight: 8 }}>{group.icon}</span>}
                            {group.groupName}
                        </Title>
                        {group.description && (
                            <Text type="secondary">{group.description}</Text>
                        )}
                    </Space>

                    {/* Field Group Form */}
                    <SingleFieldGroupForm
                        fieldGroup={group}
                        formData={formData}
                        onFieldChange={onFieldChange}
                        errors={errors}
                        readonly={readonly}
                        categoryId={categoryId}
                    />

                    {/* Divider between groups (except after last group) */}
                    {index < visibleGroups.length - 1 && (
                        <Divider style={{ margin: '32px 0' }} />
                    )}
                </div>
            ))}
        </div>
    );
};

export default InlineFieldGroups;
